<template>
  <ContentSection :spacing="index === 0 ? 'inline_text_top' : ''">
    <Container indent="wide">
      <div
        class="mb-10 flex flex-wrap justify-between gap-6 sm:mb-12 sm:gap-10"
      >
        <!-- FILTER SECTIONS -->
        <Filter
          v-if="Array.isArray(sections) && sections.length > 0"
          :terms="sections"
          :filtered="filter.section"
          :button-all-text="$t('all')"
          @filter="filter.section = $event"
        >
          {{ $t('filter.filter_for_sections') }}
        </Filter>
        <!-- FILTER LANGUAGES -->
        <template v-if="Array.isArray(languages) && languages.length > 0">
          <!-- DESKTOP -->
          <Filter
            class="hidden sm:block"
            :terms="languages"
            :filtered="filter.language"
            :button-all-text="$t('all')"
            @filter="filter.language = $event"
          >
            {{ $t('filter.filter_for_languages') }}
          </Filter>

          <!-- MOBILE -->
          <div class="relative min-h-[1.625rem] w-full sm:hidden">
            <TransitionExpand
              class="w-full overflow-hidden transition-all delay-200 duration-300 ease-in-out"
              :open="isShowingMore"
            >
              <Filter
                :terms="languages"
                :filtered="filter.language"
                :button-all-text="$t('all')"
                @filter="filter.language = $event"
              >
                {{ $t('filter.filter_for_languages') }}
              </Filter>
            </TransitionExpand>
            <transition name="fade">
              <button
                v-show="!isShowingMore"
                class="absolute left-0 top-0 flex items-center gap-2 text-base text-blue-800"
                @click="isShowingMore = true"
              >
                <PlusIcon class="w-[0.625rem] shrink-0" />
                <span>{{ $t('show_more') }}</span>
              </button>
            </transition>
          </div>
        </template>
      </div>
    </Container>

    <!-- COURSES -->
    <Container>
      <ul
        :key="rerenderKey"
        class="grid w-full grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 4xl:grid-cols-5"
      >
        <li v-for="course in courses" :key="course.id">
          <CourseCard :data="course" />
        </li>
      </ul>
      <!-- NO ENTRIES FOUND -->
      <transition name="fade">
        <div
          v-show="Array.isArray(courses) && courses.length === 0"
          class="my-10 flex w-full items-center justify-center gap-4 text-blue-dark-800 sm:my-20"
        >
          <MagnifyingLensIcon class="w-[1.125rem] shrink-0" />
          <p class="text-md text-blue-dark-800">
            {{ $t('filter.no_entries_found') }}
          </p>
        </div>
      </transition>
    </Container>
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, default: null },
  index: { type: Number, default: null },
})

const { locale } = useI18n()
const route = useRoute()
const params = useUrlSearchParams('history')
const courses = ref(null)
const sections = ref(null)
const languages = ref(null)

const isShowingMore = ref(false)

// POPULATE THE FILTER
let routeQueryFilter = route.query[`courses-overview-${props.data.id}`]

if (routeQueryFilter) {
  routeQueryFilter = JSON.parse(routeQueryFilter)
}

// Could be that the route has ha filter query when routed from a course page TypeTag
const initialFilterSection = route.query.section || null
const initialFilterLanguage = route.query.language || null

const filter = ref(
  routeQueryFilter || {
    section: initialFilterSection,
    language: initialFilterLanguage,
  }
)

// WATCHERS
watch(
  filter,
  (newValue) => {
    params[`courses-overview-${props.data.id}`] = JSON.stringify(newValue)
    fetchCourses()
  },
  { deep: true }
)

// COMPUTED

// FUNCTIONS
async function fetchCourses() {
  let payload = await useAsyncGql({
    operation: 'Courses',
    variables: {
      sort: 'date desc',
      filter: {
        site: locale.value,
        section: { contains: filter.value.section },
        languages: { contains: filter.value.language },
      },
    },
  })

  payload = payload.data.value?.entries

  if (!payload) {
    console.error('Courses could not be fetched')
    return
  }

  courses.value = payload.data
}

// INITIAL
let payload = await useAsyncGql({
  operation: 'CoursesAndTerms',
  variables: {
    sort: 'date desc',
    filter: {
      site: locale.value,
      section: { contains: filter.value.section },
      languages: { contains: filter.value.language },
    },
    filterLocale: {
      site: locale.value,
    },
  },
})

payload = payload.data?.value

if (!payload) {
  console.error('Courses could not be fetched')
}

sections.value = payload?.sections?.data

languages.value = payload?.languages?.data

courses.value = payload?.entries.data

/*
  Rerender the overview if the filter is set in the url.
  his is needed because the ISR cache provides the data without the filter applied -> This results in a wrong data mix (e.g. image and name not corresponding)
  so wie need to rerender the overview with the filter applied.
*/
const rerenderKey = ref(0)
onMounted(() => {
  if (route.query[`courses-overview-${props.data.id}`]) {
    rerenderKey.value++
  }
})
</script>
